import React, { useCallback, useState } from 'react';
import Tooltip from '../../../components/common/Tooltip';
import ViewLinkButton from '../../../components/common/ViewLinkButton/ViewLinkButton';
import { StudentTokenService } from '../../../services';
import { CheckoutLinkTrackingService } from '../../../services/CheckoutLinkTrackingService';
import {
  buildAnamneseUrl,
  buildCintaNaturalUrl,
  buildEnviarFotosUrl,
  buildEquipamentosUrl,
  buildOnboardingUrl,
  buildParqUrl,
} from '../../../utils/studentPublicLinks';
import styles from './StudentPublicLinkCopyButton.module.css';

export type StudentPublicLinkTarget =
  | 'onboarding'
  | 'cinta-natural'
  | 'anamnese'
  | 'parq'
  | 'enviar-fotos'
  | 'equipamentos'
  | 'checkout';

const TOKEN_LINK_BUILDERS: Record<Exclude<StudentPublicLinkTarget, 'checkout'>, (token: string) => string> = {
  onboarding: buildOnboardingUrl,
  'cinta-natural': buildCintaNaturalUrl,
  anamnese: buildAnamneseUrl,
  parq: buildParqUrl,
  'enviar-fotos': buildEnviarFotosUrl,
  equipamentos: buildEquipamentosUrl,
};

const COPY_LABELS: Record<StudentPublicLinkTarget, string> = {
  onboarding: 'Onboarding',
  'cinta-natural': 'Envio de materiais',
  anamnese: 'Anamnese',
  parq: 'PAR-Q',
  'enviar-fotos': 'Fotos',
  equipamentos: 'Equipamentos',
  checkout: 'Checkout',
};

const COPY_ERROR_MESSAGES: Record<StudentPublicLinkTarget, string> = {
  onboarding: 'Não foi possível copiar o link de onboarding.',
  'cinta-natural': 'Não foi possível copiar o link de envio de materiais.',
  anamnese: 'Não foi possível copiar o link de anamnese.',
  parq: 'Não foi possível copiar o link de Par-Q.',
  'enviar-fotos': 'Não foi possível copiar o link de envio de fotos.',
  equipamentos: 'Não foi possível copiar o link de equipamentos.',
  checkout: 'Não foi possível copiar o link de checkout.',
};

interface StudentPublicLinkCopyButtonProps {
  studentId: number;
  linkTarget: StudentPublicLinkTarget;
  planId?: number | null;
  label?: string;
  className?: string;
}

const StudentPublicLinkCopyButton: React.FC<StudentPublicLinkCopyButtonProps> = ({
  studentId,
  linkTarget,
  planId,
  label,
  className,
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const resolvedLabel = label ?? COPY_LABELS[linkTarget];

  const handleCopy = useCallback(async () => {
    if (isCopying) {
      return;
    }

    setIsCopying(true);

    try {
      let url: string;

      if (linkTarget === 'checkout') {
        if (!planId) {
          throw new Error('Plano não disponível para gerar o link de checkout.');
        }

        const trackedLink = await CheckoutLinkTrackingService.generate(planId, { studentId });
        url = trackedLink.url;
      } else if (linkTarget === 'enviar-fotos' || linkTarget === 'equipamentos') {
        const linkType = linkTarget === 'equipamentos' ? 'equipment' : 'body';
        const { token } = await StudentTokenService.getOrCreatePhotoUploadLink(studentId, linkType);
        url = TOKEN_LINK_BUILDERS[linkTarget](token);
      } else {
        const { token } = await StudentTokenService.generateToken(studentId);
        url = TOKEN_LINK_BUILDERS[linkTarget](token);
      }

      await navigator.clipboard.writeText(url);
      setIsCopied(true);
      window.setTimeout(() => setIsCopied(false), 2500);
    } catch (error) {
      alert(error instanceof Error ? error.message : COPY_ERROR_MESSAGES[linkTarget]);
    } finally {
      setIsCopying(false);
    }
  }, [isCopying, linkTarget, planId, studentId]);

  const tooltipLabel = `Copiar link de ${resolvedLabel}`;

  return (
    <Tooltip content={tooltipLabel}>
      <ViewLinkButton
        label={isCopying ? 'Copiando...' : isCopied ? 'Copiado' : resolvedLabel}
        icon={isCopying ? 'progress_activity' : isCopied ? 'check' : 'content_copy'}
        className={[
          className,
          isCopying ? styles.copyButtonLoading : '',
        ].filter(Boolean).join(' ')}
        onClick={() => {
          void handleCopy();
        }}
        disabled={isCopying}
        aria-label={tooltipLabel}
      />
    </Tooltip>
  );
};

export default StudentPublicLinkCopyButton;
